// iter150AZ.27 — Per-line SN picker modal for QuoteDetailPage.
//
// Pops above a single quote line when the rep clicks the "Assign" /
// "Change" chip. Loads:
//   1. ``GET /api/customers/{id}/compatible-serials?part_code=...``
//      (preferred — only compressors that list this part as a service
//      part, sorted by relevance).
//   2. ``GET /api/customers/{id}/compressors`` as a fallback so the
//      rep can still bind to any owned compressor even if no service
//      mapping exists.
//
// On pick: calls ``onPick(serialNumber)`` — the parent component
// invokes the PUT /details/{id} backend route. The modal closes
// itself.
import React, { useEffect, useState } from "react";
import { X as XIcon, Spinner, Buildings, WarningCircle } from "@phosphor-icons/react";
import api from "../lib/api";
import { useT } from "../lib/i18n";

export default function LineSerialPickerModal({
  customerId, partCode, currentSerial, onPick, onClose,
}) {
  const t = useT();
  const [loading, setLoading] = useState(false);
  const [compatible, setCompatible] = useState([]);
  const [all, setAll] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      try {
        // 1) Compatible serials (service-part match)
        let compat = [];
        if (partCode) {
          try {
            const r = await api.get(
              `/customers/${encodeURIComponent(customerId)}/compatible-serials`,
              { params: { part_code: partCode } },
            );
            if (!cancelled) {
              compat = r.data?.items || [];
              setCompatible(compat);
            }
          } catch {
            if (!cancelled) setCompatible([]);
          }
        }
        // 2) Always load the full list as the fallback option
        try {
          const r2 = await api.get(
            `/customers/${encodeURIComponent(customerId)}/compressors`,
          );
          if (!cancelled) {
            const items = (r2.data?.items || []).filter(
              c => c.serial_id && c.serial_number,
            );
            setAll(items);
            // If compatible came up empty, default to the full list
            // so the rep doesn't have to click "show all" first.
            if (compat.length === 0) setShowAll(true);
          }
        } catch (e) {
          if (!cancelled) {
            setError(e?.response?.data?.detail || "Could not load compressors");
            setAll([]);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId, partCode]);

  const list = showAll ? all : compatible;
  const showAllToggle = compatible.length > 0 && all.length > compatible.length;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="line-sn-picker-modal">
      <div className="bg-white w-full max-w-lg max-h-[85vh] rounded-sm shadow-2xl flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900">
              {t("line_sn_picker.title", "Assign Serial Number")}
            </h2>
            <p className="text-[11px] text-slate-500 font-mono">
              {partCode}
              {currentSerial && (
                <span className="ml-2 text-blue-700">
                  · {t("line_sn_picker.current", "currently SN {{sn}}", { sn: currentSerial })}
                </span>
              )}
            </p>
          </div>
          <button type="button" onClick={onClose}
                  data-testid="line-sn-picker-close"
                  className="text-slate-400 hover:text-slate-700">
            <XIcon size={16} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {error && (
            <div className="mb-3 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 text-[11px] rounded-sm flex items-start gap-2">
              <WarningCircle size={13} weight="bold" className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {loading && (
            <div className="text-[12px] text-slate-500 inline-flex items-center gap-2 py-4">
              <Spinner size={13} weight="bold" className="animate-spin" />
              {t("line_sn_picker.loading", "Loading compressors…")}
            </div>
          )}

          {!loading && !error && (
            <>
              {showAllToggle && (
                <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-wider">
                  <span className={`font-bold ${showAll ? "text-slate-500" : "text-emerald-700"}`}>
                    {showAll
                      ? t("line_sn_picker.label_all", "All owned compressors")
                      : t("line_sn_picker.label_compatible", "Recommended (service-part match)")}
                  </span>
                  <button type="button"
                          data-testid="line-sn-picker-toggle-all"
                          onClick={() => setShowAll(s => !s)}
                          className="text-blue-700 hover:text-blue-900 font-bold">
                    {showAll
                      ? t("line_sn_picker.show_compatible", "Show recommended")
                      : t("line_sn_picker.show_all", "Show all")}
                  </button>
                </div>
              )}

              {list.length === 0 ? (
                <div className="text-[12px] text-slate-600 py-6 text-center"
                     data-testid="line-sn-picker-empty">
                  {t("line_sn_picker.empty",
                     "This customer doesn't own any registered compressors.")}
                </div>
              ) : (
                <div className="border border-slate-200 rounded-sm overflow-hidden">
                  {list.map((c) => {
                    const isCurrent = c.serial_number === currentSerial;
                    return (
                      <button key={c.serial_id || c.serial_number}
                              type="button"
                              data-testid={`line-sn-picker-pick-${c.serial_number}`}
                              onClick={() => onPick(c.serial_number)}
                              disabled={isCurrent}
                              className={`w-full text-left px-3 py-2 border-b last:border-b-0 border-slate-100 text-[12px]
                                          ${isCurrent
                                            ? "bg-blue-50 cursor-not-allowed"
                                            : "hover:bg-amber-50"}`}>
                        <div className="flex items-center gap-2">
                          <Buildings size={12} weight="bold" className="text-slate-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-mono font-bold text-slate-900">
                              SN {c.serial_number || "—"}
                              {isCurrent && (
                                <span className="ml-2 text-[9px] bg-blue-600 text-white px-1 py-0.5 rounded-sm uppercase tracking-wider">
                                  {t("line_sn_picker.current_tag", "current")}
                                </span>
                              )}
                            </div>
                            <div className="text-slate-600 truncate">{c.product_name || c.product_type || "—"}</div>
                            {(c.hours != null || c.date_delivered) && (
                              <div className="text-[10px] text-slate-400">
                                {c.hours != null && <>{t("line_sn_picker.hours", "Hours")}: {c.hours}</>}
                                {c.hours != null && c.date_delivered && " · "}
                                {c.date_delivered && <>{String(c.date_delivered).slice(0, 10)}</>}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50/60 flex items-center justify-between">
          {currentSerial ? (
            <button type="button"
                    data-testid="line-sn-picker-clear"
                    onClick={() => onPick("")}
                    className="text-[10px] uppercase font-bold tracking-wider text-rose-700 hover:text-rose-900">
              {t("line_sn_picker.clear", "Clear SN binding")}
            </button>
          ) : <span />}
          <button type="button"
                  data-testid="line-sn-picker-cancel"
                  onClick={onClose}
                  className="h-8 px-3 text-[10px] uppercase font-bold tracking-wider rounded-sm
                             border border-slate-300 text-slate-700 hover:bg-slate-100">
            {t("action.cancel", "Cancel")}
          </button>
        </div>

      </div>
    </div>
  );
}
